import { useDataProvider } from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import type {
  Area,
  AreaTree,
  AreaVenueItem,
  CreateAreaBody,
  CreateAreaResponse,
  MergeAreaResponse,
  RemoveAreaAliasResponse,
  SyncAreaVenuesResponse,
  UpdateAreaBody,
} from "@/types/area";

const enc = encodeURIComponent;

/**
 * The area reads and writes the areas pages need, through the `areas` data provider's `custom`
 * (the same shape as `useSiteHealthRepository`: plain async functions, no query hooks, because
 * the callers are dialogs and batch loops rather than tables).
 *
 * Every call returns the typed response. A failure is rethrown as an `Error` carrying the
 * server's `message` (an alias owned by another area, a part with children, a comma in a name),
 * so the caller can show it as is.
 */
export const useAreaRepository = () => {
  const { custom } = useDataProvider()("areas");

  async function call<T>(
    method: "get" | "post" | "put" | "delete",
    urlPath: string,
    opts: { query?: Record<string, unknown>; payload?: unknown } = {},
  ): Promise<T> {
    try {
      const response = await custom!({
        url: "",
        method,
        query: opts.query,
        payload: opts.payload,
        meta: { urlPath },
      });
      return response.data as T;
    } catch (error) {
      throw new Error(toHumanErrorMessage(error), { cause: error });
    }
  }

  return {
    tree: (city: string, countryCode: string) =>
      call<AreaTree>("get", "tree", { query: { city, countryCode } }),

    cityAreas: (city: string, countryCode: string) =>
      call<Area[]>("get", "", { query: { city, countryCode } }),

    venues: (id: string) => call<AreaVenueItem[]>("get", `${enc(id)}/venues`),

    /** The area a name resolves to, or `null` on the server's 404. */
    resolve: async (
      city: string,
      countryCode: string,
      name: string,
    ): Promise<Area | null> => {
      try {
        return await call<Area>("get", "resolve", {
          query: { city, countryCode, name },
        });
      } catch (error) {
        if ((error as Error).cause && isNotFound((error as Error).cause)) {
          return null;
        }
        throw error;
      }
    },

    addAlias: (id: string, alias: string) =>
      call<Area>("post", `${enc(id)}/aliases`, { payload: { alias } }),

    removeAlias: (id: string, alias: string) =>
      call<RemoveAreaAliasResponse>(
        "delete",
        `${enc(id)}/aliases/${enc(alias)}`,
      ),

    /** One batch of a merge; call again until `remaining` is 0. */
    mergeBatch: (targetId: string, sourceId: string, limit = 50) =>
      call<MergeAreaResponse>("post", `${enc(targetId)}/merge`, {
        payload: { sourceId, limit },
      }),

    /** One batch of a venue re-sync; call again until `remaining` is 0. */
    syncVenuesBatch: (id: string, limit = 50) =>
      call<SyncAreaVenuesResponse>("post", `${enc(id)}/sync-venues`, {
        payload: { limit },
      }),

    update: (id: string, patch: UpdateAreaBody) =>
      call<Area>("put", enc(id), { payload: patch }),

    create: (body: CreateAreaBody) =>
      call<CreateAreaResponse>("post", "", { payload: body }),
  };
};

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;
